import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { FlowVersionT } from "src/api/flowTypes";
import { LOCK_NODE_EDITOR_CLASSNAME } from "src/authoringMultiplayerLock/constants";
import { Button } from "src/base-components/Button";
import { Modal } from "src/base-components/Modal";
import { buildUiSchemas } from "src/datasets/utils";
import { FlowVersionList } from "src/nodeEditor/SelectFlowPane/FlowVersionList";
import { FlowNodeForm } from "src/parentFlowNodes/flowNode/types";
import { setupFlowNodeForm } from "src/parentFlowNodes/utils";

type Props = {
  flowId: string;
  versions: FlowVersionT[];
  selectedVersionId: string;
  open: boolean;
  onClose: () => void;
};

export const ChangeVersionModal: React.FC<Props> = ({
  flowId,
  versions,
  selectedVersionId: initialSelectedVersioId,
  open,
  onClose,
}) => {
  const { getValues, reset } = useFormContext<FlowNodeForm>();
  const [selectedVersionId, setSelectedVersionId] = React.useState<string>(
    initialSelectedVersioId,
  );

  useEffect(() => {
    setSelectedVersionId(initialSelectedVersioId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedVersioId]);

  const handleSave = () => {
    onClose();
    const selectedVersion = versions.find(
      (version) => version.id === selectedVersionId,
    );
    if (selectedVersion) {
      const selectedSchemas = buildUiSchemas(selectedVersion);
      const [updatedFormValues] = setupFlowNodeForm(
        getValues(),
        selectedSchemas,
      );
      reset({
        ...updatedFormValues,
        child_flow_version_id: selectedVersion.id,
        child_flow_version_known_etag: selectedVersion.etag,
      });
    }
  };

  return (
    <Modal
      className={twJoin("w-120", LOCK_NODE_EDITOR_CLASSNAME)}
      headerClassName="pb-2"
      open={open}
      title="Change version"
      closeIcon
      onClose={onClose}
    >
      <div className="flex flex-col gap-y-4 px-6">
        <p className="text-gray-500 font-inter-normal-13px">
          Select the version you want to call from this Decision Flow. Keep in
          mind that Input and Output fields may require remapping.
        </p>
        <div className="mb-2 h-[489px]">
          <FlowVersionList
            flowId={flowId}
            flowVersions={versions}
            selectedVersionId={selectedVersionId}
            onSelect={(version) => setSelectedVersionId(version.id)}
          />
        </div>
      </div>
      <footer className="flex items-center justify-end gap-x-2 border-t border-gray-200 px-6 py-4">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button dataLoc="confirm-child-version-button" onClick={handleSave}>
          Call version
        </Button>
      </footer>
    </Modal>
  );
};
